/**
 * prewarmSprites — fire-and-forget colour-art cache warmer for dex grids.
 *
 * Why: the priority-dex grid (PriorityVariantDex) renders full-colour PokeAPI
 * artwork for BOTH caught and missing cells. On a cold browser cache the first
 * paint would stream sprites in one-by-one, leaving the trainer staring at a
 * half-populated grid. To match the app's "warm during the splash dwell"
 * philosophy (see App.tsx prefetchDexData), we kick off image decodes for the
 * exact ids the grid is about to show as soon as that id list is known, so the
 * grid reads from an already-warm browser cache.
 *
 * Design guarantees:
 *   - NON-BLOCKING: every load is fire-and-forget. We never await these and
 *     never gate splash dismissal or render on them. On a slow network the
 *     grid's own CaughtSprite silhouette fallback covers any straggler, so the
 *     worst case is identical to today's behaviour — never worse.
 *   - IDEMPOTENT: each URL is only requested once per session (warmed set),
 *     so repeated calls (variant toggles, re-renders, re-sampling) are cheap.
 *   - BOUNDED: callers pass only the ~40 ids actually on screen, never the
 *     full 1025, so we never flood the network with art the user won't see.
 *   - DECODE-AHEAD: we call img.decode() when available so the bitmap is ready
 *     to paint, not merely downloaded, before the grid mounts it.
 *
 * The URL sources mirror CaughtSprite's fallback chain (official-artwork PNG,
 * then HOME PNG) via the shared pokemonArtwork helpers, so a warmed URL is the
 * same URL the grid will request — a genuine cache hit, not a near-miss.
 */
import { officialArtworkUrl, homeArtworkUrl } from "@/lib/pokemonArtwork";

// Session-scoped set of already-warmed URLs. Cleared only on full reload, which
// is exactly when the browser image cache is cold again — so the two stay in
// lock-step and we never re-warm something already in cache this session.
const warmed = new Set<string>();

function warmUrl(url: string): void {
  if (warmed.has(url)) return;
  warmed.add(url);
  try {
    const img = new Image();
    // decoding=async keeps the decode off the main thread; the load itself is
    // fire-and-forget so a 404 (e.g. official-artwork missing for a form id)
    // simply never resolves the decode and is silently ignored.
    img.decoding = "async";
    img.src = url;
    // decode() (where supported) warms the decoded bitmap, not just the bytes,
    // so the eventual <img> paint is instant. Errors are swallowed: a failed
    // decode just means the grid falls back exactly as it would have anyway.
    if (typeof img.decode === "function") {
      void img.decode().catch(() => undefined);
    }
  } catch {
    /* new Image()/decode unavailable (non-DOM env, SSR test) — no-op. */
  }
}

/**
 * Prewarm the colour art for a bounded list of national-dex ids.
 *
 * @param ids     the exact ids the grid will render (already capped/sampled).
 * @param shiny   whether to warm the shiny artwork variant (shiny dex) or the
 *                normal variant (lucky dex) — must match the grid's request.
 *
 * For each id we warm the official-artwork URL (CaughtSprite's primary source)
 * AND the HOME URL (its onError fallback), so whichever the grid ends up using
 * is already cached. Fire-and-forget; returns immediately.
 */
export function prewarmDexSprites(ids: number[], shiny: boolean): void {
  for (const id of ids) {
    warmUrl(officialArtworkUrl(id, { shiny }));
    warmUrl(homeArtworkUrl(id, { shiny }));
  }
}

/** Test-only: reset the warmed-set so each test starts from a cold cache. */
export function __resetPrewarmedForTest(): void {
  warmed.clear();
}
